import { PropsWithChildren, useLayoutEffect, useRef, useState } from "react";

import { Column, useMergeRefs } from "@hightouch/htui";

import { WorkspaceValidation } from "src/components/layout/header/workspace-validation";
import { useHeaderHeight } from "src/contexts/header-height-context";
import { useIsAgenticSurfaceActive } from "src/hooks/use-agentic-home-enabled";

import { HEADER_MARGIN_TOP_PX } from "./constants";

import { PartnerBanner } from "./partner-banner";
import { SecondaryBanners } from "./secondary-banners";

export const PageBanners = ({
  disableRef = false,
  children,
}: PropsWithChildren<{
  disableRef?: boolean;
}>) => {
  const { headerElementRef } = useHeaderHeight();
  const columnRef = useRef<HTMLDivElement>(null);
  const isAgenticSurfaceActive = useIsAgenticSurfaceActive();

  const [hasContent, setHasContent] = useState(false);

  const mergedRef = useMergeRefs(
    columnRef,
    disableRef ? undefined : headerElementRef,
  );

  // Use layout effect to avoid layout shift when navigating
  useLayoutEffect(() => {
    const element = columnRef.current;
    if (!element) return;

    const update = () => setHasContent(element.childElementCount > 0);
    update();

    const observer = new MutationObserver(update);
    observer.observe(element, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  return (
    <Column
      ref={mergedRef}
      gap={2}
      mr={2}
      ml={1}
      mt={hasContent ? `${HEADER_MARGIN_TOP_PX}px` : 0}
      // On agentic gradient surfaces, lift banners above the fixed zIndex-0 background gradient.
      position={isAgenticSurfaceActive ? "relative" : undefined}
      zIndex={isAgenticSurfaceActive ? 1 : undefined}
    >
      <PartnerBanner />
      <WorkspaceValidation />
      <SecondaryBanners />
      {children}
    </Column>
  );
};
